<script lang="ts">
const COLOR_OPTIONS = [
  { name: 'red', hex: '#ef4444' },
  { name: 'rose', hex: '#f43f5e' },
  { name: 'pink', hex: '#ec4899' },
  { name: 'fuchsia', hex: '#d946ef' },
  { name: 'purple', hex: '#a855f7' },
  { name: 'violet', hex: '#8b5cf6' },
  { name: 'indigo', hex: '#6366f1' },
  { name: 'blue', hex: '#3b82f6' },
  { name: 'sky', hex: '#0ea5e9' },
  { name: 'cyan', hex: '#06b6d4' },
  { name: 'teal', hex: '#14b8a6' },
  { name: 'emerald', hex: '#10b981' },
  { name: 'green', hex: '#22c55e' },
  { name: 'lime', hex: '#84cc16' },
  { name: 'yellow', hex: '#eab308' },
  { name: 'amber', hex: '#f59e0b' },
  { name: 'orange', hex: '#f97316' },
]

function getHex(name: string): string {
  return COLOR_OPTIONS.find(c => c.name === name)?.hex || '#ef4444'
}
</script>

<script setup lang="ts">
// `useAppConfig()` type `ui` d'après `app.config.ts` ; la palette Nuxt UI y est
// injectée par le module, hors de ce que voit le DS. Cette forme locale décrit
// le seul chemin utilisé ici.
type ConfigCouleurs = { colors?: { primary?: string } }

const isDev = import.meta.dev
const appConfig = useAppConfig()
const isOpen = ref(false)

const uiConfig = appConfig.ui as ConfigCouleurs | undefined

const defaultColor = uiConfig?.colors?.primary || 'red'
const selectedColor = computed(() => uiConfig?.colors?.primary || 'red')

function setColor(color: string) {
  if (uiConfig?.colors) uiConfig.colors.primary = color
}
</script>

<template>
  <div v-if="isDev" class="relative">
    <UTooltip text="Couleur (dev)">
      <UButton
        icon="i-lucide-palette"
        variant="ghost"
        color="neutral"
        size="sm"
        class="text-white hover:bg-white/15"
        @click="() => { isOpen = !isOpen }"
      />
    </UTooltip>

    <!-- Backdrop invisible pour fermer au clic dehors -->
    <div v-if="isOpen" class="fixed inset-0 z-40" @click="() => { isOpen = false }" />

    <div
      v-if="isOpen"
      class="absolute right-0 top-full mt-2 z-50 bg-[var(--ui-bg-elevated)] border border-[var(--ui-border)] rounded-lg shadow-xl p-3 w-64"
    >
      <!-- Couleur définie dans le thème -->
      <div class="mb-3 pb-3 border-b border-[var(--ui-border)]">
        <p class="text-[10px] uppercase tracking-wide text-[var(--ui-text-dimmed)] mb-1.5">Couleur définie dans le thème</p>
        <button
          class="flex items-center gap-2 w-full px-2 py-1.5 rounded-md hover:bg-[var(--ui-bg-accented)] transition-colors cursor-pointer"
          @click="setColor(defaultColor)"
        >
          <span
            class="w-7 h-7 rounded-md shrink-0 flex items-center justify-center"
            :style="{ backgroundColor: getHex(defaultColor) }"
          >
            <UIcon v-if="selectedColor === defaultColor" name="i-lucide-check" class="text-white text-sm" />
          </span>
          <span class="text-sm font-medium text-[var(--ui-text-highlighted)]">{{ defaultColor }}</span>
        </button>
      </div>

      <!-- Couleur actuellement active -->
      <p class="text-[10px] uppercase tracking-wide text-[var(--ui-text-dimmed)] mb-1.5">
        Couleur active : <strong class="text-[var(--ui-text-highlighted)] normal-case">{{ selectedColor }}</strong>
      </p>

      <!-- Toutes les couleurs -->
      <div class="grid grid-cols-6 gap-3 mt-2">
        <button
          v-for="color in COLOR_OPTIONS"
          :key="color.name"
          class="w-7 h-7 rounded-md transition-all flex items-center justify-center cursor-pointer"
          :style="{ backgroundColor: color.hex }"
          :class="selectedColor === color.name ? 'ring-2 ring-inset ring-white scale-110' : 'hover:scale-110 opacity-75 hover:opacity-100'"
          :title="color.name"
          @click="setColor(color.name)"
        >
          <UIcon v-if="selectedColor === color.name" name="i-lucide-check" class="text-white text-sm" />
        </button>
      </div>
      <p class="text-[10px] text-[var(--ui-text-dimmed)] mt-3">
        Dev uniquement — invisible en prod
      </p>
    </div>
  </div>
</template>
